import { useRouter } from 'next/navigation'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { changeRouterPage } from '@/shared/lib/utils/routing/changeRouterPage'

type PaginationContextType = {
	currentPage: number
	goToPage: (pageNumber: number, shallow?: boolean) => void
	setLastPage: (value: number) => void
	nextPage: () => void
	prevPage: () => void
	totalProducts: number
	lastPage: number
	IS_LAST_PAGE?: boolean
	loadMore: () => void
}
type PaginationProviderProps = {
	currentPage: number
	totalProducts: number
	lastPage: number
	isLastPage?: boolean
	children: JSX.Element
}
const PaginationContext = createContext<PaginationContextType | null>(null)

PaginationContext.displayName = `PaginationContext`
/**
 * Pagination Provider
 * @param props
 * @param props.children
 * @param props.currentPage - current page
 * @param props.totalProducts - total products
 * @param props.lastPage - last page
 * @param props.isLastPage - boolean flag last page
 */
export const PaginationProvider = ({
	children,
	currentPage: initialCurrentPage = 1,
	totalProducts = 0,
	lastPage: initialLastPage,
	isLastPage
}: PaginationProviderProps): JSX.Element => {
	const [currentPage, setCurrentPage] = useState<number>(initialCurrentPage)
	const [lastPage, setLastPage] = useState<number>(initialLastPage)

	const router = useRouter()

	useEffect(() => {
		setCurrentPage(initialCurrentPage)
		setLastPage(initialLastPage)
	}, [initialCurrentPage, initialLastPage])

	const goToPage = useCallback(
		async (pageNumber: number, shallow = false, preventScroll = false) => {
			await changeRouterPage(router, pageNumber, shallow, preventScroll)
			setCurrentPage(pageNumber)
		},
		[router]
	)
	const loadMore = useCallback(async () => {
		if (currentPage < lastPage) {
			await goToPage(currentPage + 1, true, false)
		}
	}, [currentPage, lastPage, goToPage])
	const nextPage = useCallback(async () => {
		if (currentPage < lastPage) {
			await goToPage(currentPage + 1, true, false)
		}
	}, [currentPage, lastPage, goToPage])

	const prevPage = useCallback(async () => {
		if (currentPage > 1) {
			await goToPage(currentPage - 1, true, false)
		}
	}, [currentPage, goToPage])

	const value = useMemo(
		() => ({
			currentPage,
			goToPage,
			nextPage,
			prevPage,
			totalProducts,
			lastPage,
			IS_LAST_PAGE: isLastPage,
			setLastPage,
			loadMore
		}),
		[currentPage, goToPage, nextPage, prevPage, totalProducts, lastPage, isLastPage, setLastPage, loadMore]
	)

	return <PaginationContext.Provider value={value}>{children}</PaginationContext.Provider>
}

export const usePagination = (): PaginationContextType => {
	const context = useContext(PaginationContext)

	if (!context) {
		throw new Error(`must be used within a PaginationProvider`)
	}

	return context
}
